// ClassificationDetailModal - Detail view for a single classification
// Includes email content, LLM classification, sender match, order info,
// and human verification workflow.

import { useState } from 'react';
import { emailMonitorDecisionService } from '../../services/emailMonitorDecisions';
import type { EmailClassificationResult, EmailCategory } from '../../services/emailMonitorDecisions';
import styles from './ClassificationDetailModal.module.css';

const CATEGORY_COLORS: Record<string, string> = {
  order: '#27ae60',
  quote: '#094290',
  inquiry: '#8e44ad',
  support: '#e67e22',
  payment: '#16a085',
  shipping: '#d35400',
  internal: '#7f8c8d',
  spam: '#c0392b',
  other: '#95a5a6',
};

const CONFIDENCE_BUCKET_COLORS: Record<'high' | 'medium' | 'low', string> = {
  high: '#27ae60',
  medium: '#e67e22',
  low: '#c0392b',
};

interface Props {
  result: EmailClassificationResult | null;
  loading: boolean;
  categories: EmailCategory[];
  onClose: () => void;
  onVerified: (updated: EmailClassificationResult) => void;
}

export default function ClassificationDetailModal({
  result,
  loading,
  categories,
  onClose,
  onVerified,
}: Props) {
  const [emailBodyView, setEmailBodyView] = useState<'text' | 'html'>('text');
  const [verifyCategory, setVerifyCategory] = useState('');
  const [verifyNotes, setVerifyNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<'overview' | 'email' | 'order'>('overview');

  // Reset verify state when result changes
  const initVerify = (r: EmailClassificationResult) => {
    setVerifyCategory(r.human_category || r.llm_category);
    setVerifyNotes(r.human_notes || '');
    setEmailBodyView('text');
    setTab('overview');
  };

  // Call on first render with result
  if (result && verifyCategory === '' && !result.human_verified) {
    initVerify(result);
  }

  const saveVerification = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const updated = await emailMonitorDecisionService.verifyClassification(
        result.id,
        verifyCategory || undefined,
        verifyNotes || undefined,
      );
      onVerified(updated);
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <h2 className={styles.title}>
              {loading ? 'Loading...' : result?.email_subject || 'Classification Details'}
            </h2>
            <button className={styles.closeBtn} onClick={onClose}>&times;</button>
          </div>
          {result && (
            <div className={styles.meta}>
              <span>From: {result.email_from || '-'}</span>
              <span>ID: {result.id.slice(0, 8)}</span>
              <span>{new Date(result.created_at).toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Tabs */}
        {result && (
          <div className={styles.tabBar}>
            <button
              className={`${styles.tab} ${tab === 'overview' ? styles.tabActive : ''}`}
              onClick={() => setTab('overview')}
            >
              Overview
            </button>
            {(result.email_body_text || result.email_body_html) && (
              <button
                className={`${styles.tab} ${tab === 'email' ? styles.tabActive : ''}`}
                onClick={() => setTab('email')}
              >
                Email Content
              </button>
            )}
            {result.llm_order_info && Object.keys(result.llm_order_info).length > 0 && (
              <button
                className={`${styles.tab} ${tab === 'order' ? styles.tabActive : ''}`}
                onClick={() => setTab('order')}
              >
                Order Info
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className={styles.body}>
          {loading ? (
            <div className={styles.loading}>Loading classification details...</div>
          ) : result && (
            <>
              {/* Overview Tab */}
              {tab === 'overview' && (
                <div className={styles.overviewTab}>
                  {/* LLM Classification */}
                  <section className={styles.section}>
                    <h3>LLM Classification</h3>
                    <div className={styles.grid}>
                      <div className={styles.field}>
                        <label>Category</label>
                        <span
                          className={styles.catBadge}
                          style={{ background: CATEGORY_COLORS[result.llm_category] || '#95a5a6' }}
                        >
                          {result.llm_category}
                        </span>
                      </div>
                      <div className={styles.field}>
                        <label>Confidence</label>
                        {result.confidence_bucket ? (
                          <span
                            className={styles.catBadge}
                            style={{ background: CONFIDENCE_BUCKET_COLORS[result.confidence_bucket] }}
                            title={`Raw LLM score: ${Math.round(result.llm_confidence * 100)}%`}
                          >
                            {result.confidence_bucket}
                          </span>
                        ) : (
                          <span title="No deterministic bucket on this row (pre-rollout)">
                            {Math.round(result.llm_confidence * 100)}%
                          </span>
                        )}
                      </div>
                      <div className={styles.field}>
                        <label>Effective</label>
                        <span
                          className={styles.catBadge}
                          style={{ background: CATEGORY_COLORS[result.effective_category] || '#95a5a6' }}
                        >
                          {result.effective_category}
                          {result.human_verified && ' \u2713'}
                        </span>
                      </div>
                    </div>
                    {result.llm_sub_categories && result.llm_sub_categories.length > 0 && (
                      <div className={styles.fieldFull}>
                        <label>Sub-categories</label>
                        <div className={styles.tagList}>
                          {result.llm_sub_categories.map((s, i) => (
                            <span key={i} className={styles.tag}>{s}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {result.llm_summary && (
                      <div className={styles.fieldFull}>
                        <label>Summary</label>
                        <p className={styles.textBlock}>{result.llm_summary}</p>
                      </div>
                    )}
                    {result.llm_classification_reason && (
                      <div className={styles.fieldFull}>
                        <label>Reason</label>
                        <p className={styles.textBlock}>{result.llm_classification_reason}</p>
                      </div>
                    )}
                    <div className={styles.grid}>
                      <div className={styles.field}>
                        <label>From Customer</label>
                        <span>{result.llm_is_from_customer ? 'Yes' : 'No'}</span>
                      </div>
                      <div className={styles.field}>
                        <label>Needs Response</label>
                        <span>{result.llm_requires_response ? 'Yes' : 'No'}</span>
                      </div>
                      <div className={styles.field}>
                        <label>Urgent</label>
                        <span>{result.llm_is_urgent ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </section>

                  {/* Sender Match */}
                  <section className={styles.section}>
                    <h3>Sender Match</h3>
                    <div className={styles.grid}>
                      <div className={styles.field}>
                        <label>Matched</label>
                        <span className={result.sender_matched ? styles.matchYes : styles.matchNo}>
                          {result.sender_matched ? 'Yes' : 'No'}
                        </span>
                      </div>
                      {result.sender_matched && (
                        <>
                          <div className={styles.field}>
                            <label>Method</label>
                            <span>{result.sender_match_method || '-'}</span>
                          </div>
                          <div className={styles.field}>
                            <label>Customer</label>
                            <span>
                              {result.matched_customer_name || '-'}
                              {result.matched_customer_no && ` (${result.matched_customer_no})`}
                            </span>
                          </div>
                          <div className={styles.field}>
                            <label>Salesperson</label>
                            <span>
                              {result.matched_salesperson_name || '-'}
                              {result.matched_salesperson_no && ` (${result.matched_salesperson_no})`}
                            </span>
                          </div>
                        </>
                      )}
                      {result.sender_match_reason && (
                        <div className={styles.fieldFull}>
                          <label>Reason</label>
                          <span>{result.sender_match_reason}</span>
                        </div>
                      )}
                    </div>
                  </section>

                  {/* CSR Assignment */}
                  {result.csr_name && (
                    <section className={styles.section}>
                      <h3>CSR Assignment</h3>
                      <div className={styles.grid}>
                        <div className={styles.field}>
                          <label>Assigned CSR</label>
                          <span className={styles.matchYes}>{result.csr_name}</span>
                        </div>
                        <div className={styles.field}>
                          <label>Type</label>
                          <span>{result.csr_assignment_type ? result.csr_assignment_type.charAt(0).toUpperCase() + result.csr_assignment_type.slice(1) : 'N/A'}</span>
                        </div>
                        <div className={styles.field}>
                          <label>Confidence</label>
                          <span>
                            {result.csr_confidence !== null
                              ? `${Math.round(result.csr_confidence * 100)}%`
                              : '-'}
                          </span>
                        </div>
                        <div className={styles.field}>
                          <label>Method</label>
                          <span>{result.csr_method || '-'}</span>
                        </div>
                        {result.csr_evidence && (
                          <div className={styles.fieldFull}>
                            <label>Evidence</label>
                            <span>{result.csr_evidence}</span>
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  {/* Human Verification */}
                  <section className={styles.section}>
                    <h3>Human Verification</h3>
                    {result.human_verified ? (
                      <div className={styles.grid}>
                        <div className={styles.field}>
                          <label>Verified Category</label>
                          <span
                            className={styles.catBadge}
                            style={{
                              background: CATEGORY_COLORS[result.human_category || result.llm_category] || '#95a5a6',
                            }}
                          >
                            {result.human_category || result.llm_category} \u2713
                          </span>
                        </div>
                        <div className={styles.field}>
                          <label>Verified By</label>
                          <span>{result.human_verified_by || '-'}</span>
                        </div>
                        <div className={styles.field}>
                          <label>Verified At</label>
                          <span>
                            {result.human_verified_at
                              ? new Date(result.human_verified_at).toLocaleString()
                              : '-'}
                          </span>
                        </div>
                        {result.human_notes && (
                          <div className={styles.fieldFull}>
                            <label>Notes</label>
                            <span>{result.human_notes}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={styles.verifyForm}>
                        <p className={styles.verifyPrompt}>
                          Confirm or correct the LLM classification:
                        </p>
                        <div className={styles.verifyFields}>
                          <div className={styles.verifyField}>
                            <label>Category</label>
                            <select
                              value={verifyCategory}
                              onChange={e => setVerifyCategory(e.target.value)}
                            >
                              {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                          </div>
                          <div className={styles.verifyField}>
                            <label>Notes (optional)</label>
                            <input
                              type="text"
                              value={verifyNotes}
                              onChange={e => setVerifyNotes(e.target.value)}
                              placeholder="Any notes..."
                            />
                          </div>
                        </div>
                        <button
                          className={styles.verifyBtn}
                          onClick={saveVerification}
                          disabled={saving}
                        >
                          {saving ? 'Saving...' : 'Verify Classification'}
                        </button>
                      </div>
                    )}
                  </section>

                  {/* Metadata */}
                  <section className={styles.section}>
                    <h3>Metadata</h3>
                    <div className={styles.grid}>
                      <div className={styles.field}>
                        <label>Model</label>
                        <span>{result.model_used}</span>
                      </div>
                      <div className={styles.field}>
                        <label>Tokens</label>
                        <span>{result.input_tokens} in / {result.output_tokens} out</span>
                      </div>
                      <div className={styles.field}>
                        <label>Latency</label>
                        <span>{result.latency_ms}ms</span>
                      </div>
                    </div>
                  </section>
                </div>
              )}

              {/* Email Content Tab */}
              {tab === 'email' && (
                <div className={styles.emailTab}>
                  {/* Toggle */}
                  {result.email_body_text && result.email_body_html && (
                    <div className={styles.bodyToggle}>
                      <button
                        className={`${styles.toggleBtn} ${emailBodyView === 'text' ? styles.toggleActive : ''}`}
                        onClick={() => setEmailBodyView('text')}
                      >
                        Plain Text
                      </button>
                      <button
                        className={`${styles.toggleBtn} ${emailBodyView === 'html' ? styles.toggleActive : ''}`}
                        onClick={() => setEmailBodyView('html')}
                      >
                        HTML
                      </button>
                    </div>
                  )}
                  <div className={styles.emailContent}>
                    {emailBodyView === 'html' && result.email_body_html ? (
                      <iframe
                        className={styles.emailIframe}
                        srcDoc={`<head><base target="_blank" rel="noopener noreferrer"></head>${result.email_body_html}`}
                        sandbox="allow-popups allow-popups-to-escape-sandbox"
                        title="Email HTML content"
                      />
                    ) : (
                      <pre className={styles.emailText}>
                        {result.email_body_text || '(No plain text content)'}
                      </pre>
                    )}
                  </div>
                </div>
              )}

              {/* Order Info Tab */}
              {tab === 'order' && result.llm_order_info && (() => {
                const oi = result.llm_order_info!;
                const poNumber = 'po_number' in oi ? String(oi.po_number) : null;
                const shipDate = 'requested_ship_date' in oi ? String(oi.requested_ship_date) : null;
                const estValue = 'estimated_value' in oi && oi.estimated_value != null ? Number(oi.estimated_value) : null;
                const urgency = 'urgency' in oi ? String(oi.urgency) : null;
                const shipTo = 'ship_to_address' in oi ? String(oi.ship_to_address) : null;
                const specialInstr = 'special_instructions' in oi ? String(oi.special_instructions) : null;
                return (
                <div className={styles.orderTab}>
                  <div className={styles.grid}>
                    {poNumber && (
                      <div className={styles.field}>
                        <label>PO Number</label>
                        <span className={styles.orderValue}>{poNumber}</span>
                      </div>
                    )}
                    {shipDate && (
                      <div className={styles.field}>
                        <label>Ship Date</label>
                        <span>{shipDate}</span>
                      </div>
                    )}
                    {estValue != null && (
                      <div className={styles.field}>
                        <label>Estimated Value</label>
                        <span className={styles.orderValue}>
                          ${estValue.toLocaleString()}
                        </span>
                      </div>
                    )}
                    {urgency && (
                      <div className={styles.field}>
                        <label>Urgency</label>
                        <span>{urgency}</span>
                      </div>
                    )}
                  </div>
                  {shipTo && (
                    <div className={styles.fieldFull}>
                      <label>Ship-to Address</label>
                      <span>{shipTo}</span>
                    </div>
                  )}
                  {specialInstr && (
                    <div className={styles.fieldFull}>
                      <label>Special Instructions</label>
                      <span>{specialInstr}</span>
                    </div>
                  )}
                  {Array.isArray(oi.line_items) &&
                    (oi.line_items as Array<Record<string, unknown>>).length > 0 && (
                      <div className={styles.fieldFull}>
                        <label>Line Items</label>
                        <table className={styles.lineItemsTable}>
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Code</th>
                              <th>Qty</th>
                              <th>Unit</th>
                              <th>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(oi.line_items as Array<Record<string, unknown>>).map((item, i) => (
                              <tr key={i}>
                                <td>{String(item.product_name || '-')}</td>
                                <td>{String(item.product_code || '-')}</td>
                                <td>{item.quantity != null ? String(item.quantity) : '-'}</td>
                                <td>{String(item.unit || '-')}</td>
                                <td>{String(item.notes || '-')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                </div>
                );
              })()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
